import React, {useMemo, useRef, useState} from 'react';
import OeTip from './OeTip';
import OeSelect from './OeSelect';
import {docUrl, printDocument} from './api';

// Beleg-Panel: Kopfzeile mit Drucker-Auswahl, Kopien, „an Drucker senden",
// Browser-Druck und PDF-Download, darunter die Vorschau im iframe (Original
// `docPanelHtml`/`bindDocPanel`, 3496–3552; ABGLEICH E63).
//
// Einmal gebaut, zweimal benutzt: Kommissionierschein (Schritt 4) und
// Schneider-Lieferschein (Schritt 3) sollen sich gleich anfühlen — gleiche
// Knöpfe an gleicher Stelle.

/**
 * Vorauswahl des Druckers (Original 3500): 1) persönlicher Standard,
 * 2) sonst ein Lager-Drucker, 3) sonst der erste.
 */
function vorauswahl(printers, preferred, fixed) {
    const ausPref = preferred ? printers.indexOf(preferred) : -1;
    if (ausPref >= 0) {
        return {index: ausPref, fromPref: true, fixed: !!fixed};
    }
    const lager = printers.findIndex((p) => /lager/i.test(p));
    return {index: Math.max(0, lager), fromPref: false, fixed: false};
}

export default function DocPanel({
    doc,
    icon,
    label,
    hint,
    orderRef,
    source,
    kommState,
    canPrint,
    onPreferred,
    extra,
}) {
    const printers = useMemo(() => (kommState && kommState.printers) || [], [kommState]);
    const start = useMemo(() => vorauswahl(printers, kommState && kommState.preferred, kommState && kommState.preferredFixed), [printers, kommState]);

    const [printer, setPrinter] = useState(() => printers[start.index] || '');
    const [copies, setCopies] = useState('1');
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const frameRef = useRef(null);

    const gewaehlt = printer || printers[start.index] || '';
    const url = docUrl(orderRef, doc.docId, {...source, download: false});

    const browserDruck = () => {
        const f = frameRef.current;
        try {
            f.contentWindow.focus();
            f.contentWindow.print();
        } catch (e) {
            // Manche Browser sperren den Druck aus dem iframe — dann als
            // eigenen Tab öffnen und dort drucken.
            window.open(url, '_blank', 'noopener');
        }
    };

    const herunterladen = () => {
        const a = document.createElement('a');
        a.href = docUrl(orderRef, doc.docId, {...source, download: true});
        a.download = doc.name || 'beleg.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const drucken = async () => {
        const quantity = Math.max(1, Math.min(20, parseInt(copies, 10) || 1));
        setBusy(true);
        setStatus({tone: 'work', text: `Druckauftrag wird an „${gewaehlt}" geschickt…`});
        try {
            const j = await printDocument(orderRef, {documentId: doc.docId, printerName: gewaehlt, quantity});
            if (j.dryRun) {
                setStatus({tone: 'warn', text: `Trockenlauf: ${j.message || 'Es wurde nichts gedruckt.'}`});
                return;
            }
            if (j.ok) {
                setStatus({
                    tone: 'ok',
                    text: `✓ ${quantity}× an „${j.printerName}" geschickt (Status ${j.status})`
                        + (start.fixed ? '.' : ' — als dein Standarddrucker gemerkt.'),
                });
                if (onPreferred && j.printerName && !start.fixed) {
                    onPreferred(j.printerName);
                }
                return;
            }
            setStatus({tone: 'err', text: 'Druck fehlgeschlagen: ' + (j.error || 'unbekannt')});
        } catch (e) {
            const meldung = e.response?.data?.error || e.message;
            setStatus({tone: 'err', text: 'Druck fehlgeschlagen: ' + meldung});
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="kommhead">
                <span className="lbl">{icon} {label}</span>
                <span className="muted">{doc.name || ''}</span>
                <span className="sp"/>

                {printers.length && canPrint ? (
                    <>
                        <OeTip tip={start.fixed
                            ? 'Standard aus deinem Benutzerkonto — dieser Drucker ist immer vorausgewählt. '
                                + 'Ändern kannst du ihn unter „Profil“ bzw. in der Benutzerverwaltung.'
                            : start.fromPref
                            ? 'Dein zuletzt benutzter Drucker ist vorausgewählt.'
                            : 'Kein persönlicher Standard gesetzt — es ist ein Lager-Drucker vorgeschlagen. Nach dem '
                                + 'ersten Druck wird deine Wahl gemerkt.'}>
                            <span className="muted">Drucker{start.fromPref ? ' ★' : ''}:</span>
                        </OeTip>
                        <span style={{maxWidth: 220, display: 'inline-block'}}>
                            <OeSelect
                                value={gewaehlt}
                                options={printers.map((p) => ({value: p, label: p}))}
                                onChange={setPrinter}
                                ariaLabel="Drucker wählen"
                            />
                        </span>
                        <OeTip tip="Anzahl Kopien (1 bis 20).">
                            <input
                                className="rngi"
                                style={{width: 44}}
                                value={copies}
                                inputMode="numeric"
                                autoComplete="off"
                                aria-label="Anzahl Kopien"
                                onChange={(e) => setCopies(e.target.value)}
                            />
                        </OeTip>
                        <OeTip tip={`Schickt das PDF direkt an den Drucker (weclapp-Druck-Agent „${(kommState && kommState.osName) || ''}") `
                            + 'ohne Browser-Dialog. Der Ausdruck läuft physisch im Lager — im Testmodus wird nur '
                            + 'protokolliert („Trockenlauf").'}>
                            <button type="button" className="actbtn" disabled={busy || !gewaehlt} onClick={drucken}>
                                🖨 an Drucker senden
                            </button>
                        </OeTip>
                    </>
                ) : (
                    <span className="muted">
                        {canPrint ? 'kein Druck-Agent' : 'Druck nur mit dem Recht „Lager"'}
                    </span>
                )}

                <OeTip tip="Öffnet den Druckdialog des Browsers für dieses PDF. Es wird nichts an den Lager-Drucker geschickt.">
                    <button type="button" className="actbtn" onClick={browserDruck}>Browser-Druck</button>
                </OeTip>
                <OeTip tip="Lädt das PDF als Datei herunter. In weclapp ändert sich nichts.">
                    <button type="button" className="actbtn" onClick={herunterladen}>⬇ PDF</button>
                </OeTip>
                {extra}
            </div>

            <iframe ref={frameRef} className="kommframe" src={url} title={label}/>

            {hint ? <div className="kommhelp">{hint}</div> : null}
            {status ? <div className={`status ${status.tone}`} style={{marginTop: 6}}>{status.text}</div> : null}
        </>
    );
}
